import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FiZap, 
  FiShield, 
  FiTrendingUp, 
  FiUsers, 
  FiCheckCircle,
  FiArrowRight,
  FiMonitor,
  FiAlertTriangle,
  FiSettings
} from 'react-icons/fi';
import { trackPageView } from '../utils/analytics';
import './AgentSRE.css';

const AgentSRE = () => {
  useEffect(() => {
    // Track page visit with comprehensive analytics
    trackPageView('/agent-sre', {
      page_title: 'LogCrest AgentSRE - Intelligent SRE Operations',
      page_category: 'agent-sre'
    });
  }, []);

  const features = [
    {
      icon: <FiZap />,
      title: 'Intelligent Automation',
      description: 'AI-powered automation that learns from your infrastructure patterns and automatically resolves common issues.'
    },
    {
      icon: <FiShield />,
      title: 'Proactive Security',
      description: 'Advanced threat detection and security monitoring with automated response capabilities.'
    },
    {
      icon: <FiTrendingUp />,
      title: 'Performance Optimization',
      description: 'Continuous performance monitoring with intelligent recommendations for optimization.'
    },
    {
      icon: <FiUsers />,
      title: 'Team Collaboration',
      description: 'Enhanced collaboration tools with role-based access and shared incident management.'
    }
  ];

  const capabilities = [
    'Automated incident response and resolution',
    'Predictive failure detection',
    'Intelligent capacity planning',
    'Security vulnerability scanning',
    'Performance bottleneck identification',
    'Cost optimization recommendations',
    'Compliance monitoring and reporting',
    'Custom workflow automation'
  ];

  const benefits = [
    {
      metric: '75%',
      label: 'Reduction in MTTR',
      description: 'Mean Time To Resolution'
    },
    {
      metric: '90%',
      label: 'Fewer False Alerts',
      description: 'AI-powered alert filtering'
    },
    {
      metric: '60%',
      label: 'Cost Savings',
      description: 'Through intelligent optimization'
    },
    {
      metric: '99.9%',
      label: 'Uptime Guarantee',
      description: 'With proactive monitoring'
    }
  ];

  return (
    <div className="agent-sre">
      {/* Hero Section */}
      <section className="agent-hero">
        <div className="container">
          <div className="agent-hero-content">
            <motion.div
              className="hero-text"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>AgentSRE: AI-Powered Site Reliability Engineering</h1>
              <p>
                Transform your SRE operations with intelligent automation, proactive monitoring, 
                and AI-driven insights. Reduce incidents, improve performance, and scale with confidence.
              </p>
              <div className="hero-buttons">
                <Link to="/pricing" className="btn btn-secondary">
                  Get Started
                  <FiArrowRight />
                </Link>
              </div>
            </motion.div>
            <motion.div
              className="hero-visual"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="dashboard-mockup">
                <div className="mockup-header">
                  <div className="mockup-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span className="mockup-title">LogCrest AgentSRE Dashboard</span>
                </div>
                <div className="mockup-content">
                  <div className="metric-row">
                    <div className="metric-item">
                      <div className="metric-value">99.9%</div>
                      <div className="metric-label">Uptime</div>
                    </div>
                    <div className="metric-item">
                      <div className="metric-value">2.3s</div>
                      <div className="metric-label">Response</div>
                    </div>
                  </div>
                  <div className="alert-panel">
                    <div className="alert-item resolved">
                      <FiCheckCircle />
                      <span>Database connection restored</span>
                    </div>
                    <div className="alert-item warning">
                      <FiAlertTriangle />
                      <span>High CPU usage detected</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="agent-features">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Powerful SRE capabilities at your fingertips</h2>
            <div className="grid grid-2">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="feature-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="feature-icon">
                    {feature.icon}
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="agent-capabilities">
        <div className="container">
          <div className="capabilities-content">
            <motion.div
              className="capabilities-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Comprehensive SRE capabilities</h2>
              <p>
                AgentSRE provides everything you need to run reliable, scalable infrastructure. 
                From automated incident response to predictive analytics, we've got you covered.
              </p>
              <ul className="capabilities-list">
                {capabilities.map((capability, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FiCheckCircle />
                    {capability}
                  </motion.li>
                ))}
              </ul>
              <Link to="/demo" className="btn btn-primary">
                See AgentSRE in Action
                <FiArrowRight />
              </Link>
            </motion.div>
            <motion.div
              className="capabilities-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="workflow-diagram">
                <div className="workflow-step">
                  <div className="step-icon">
                    <FiMonitor />
                  </div>
                  <div className="step-content">
                    <h4>Monitor</h4>
                    <p>Continuous monitoring of all systems</p>
                  </div>
                </div>
                <div className="workflow-arrow">→</div>
                <div className="workflow-step">
                  <div className="step-icon">
                    <FiAlertTriangle />
                  </div>
                  <div className="step-content">
                    <h4>Detect</h4>
                    <p>AI-powered anomaly detection</p>
                  </div>
                </div>
                <div className="workflow-arrow">→</div>
                <div className="workflow-step">
                  <div className="step-icon">
                    <FiSettings />
                  </div>
                  <div className="step-content">
                    <h4>Automate</h4>
                    <p>Intelligent response and resolution</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="agent-benefits">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Measurable results for your team</h2>
            <div className="benefits-grid">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="benefit-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="benefit-metric">{benefit.metric}</div>
                  <div className="benefit-label">{benefit.label}</div>
                  <div className="benefit-description">{benefit.description}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="agent-cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Ready to revolutionize your SRE operations?</h2>
            <p>
              Join leading teams who trust LogCrest AgentSRE to keep their infrastructure running smoothly. 
              Start your free trial today.
            </p>
            <div className="cta-buttons">
              <Link to="/pricing" className="btn btn-primary">
                Contact Sales
                <FiArrowRight />
              </Link>
              <Link to="/demo" className="btn btn-secondary">
                Schedule Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AgentSRE;
